import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Tag, Space, Modal, Form, Input, InputNumber, Select, DatePicker, Switch, message, Divider } from 'antd';
import { PlusOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getPublishTasks, createPublishTask, cancelTask, retryTask, getVideos, getAccounts } from '../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const PublishManage = () => {
  const [tasks, setTasks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadTasks();
    loadVideos();
    loadAccounts();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await getPublishTasks({ limit: 50 });
      if (response.success) {
        setTasks(response.data.list);
      }
    } catch (error) {
      console.error('加载任务失败:', error);
    }
  };

  const loadVideos = async () => {
    try {
      const response = await getVideos({ limit: 100 });
      if (response.success) {
        setVideos(response.data.list);
      }
    } catch (error) {
      console.error('加载视频失败:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await getAccounts({ limit: 100 });
      if (response.success) {
        setAccounts(response.data.list);
      }
    } catch (error) {
      console.error('加载账号失败:', error);
    }
  };

  // 创建发布任务
  const handleCreateTask = async (values) => {
    setLoading(true);
    try {
      const response = await createPublishTask({
        ...values,
        scheduledTime: values.scheduledTime?.format('YYYY-MM-DD HH:mm:ss'),
        accountIds: values.accountIds
      });

      if (response.success) {
        message.success('发布任务创建成功！');
        setModalVisible(false);
        form.resetFields();
        loadTasks();
      }
    } catch (error) {
      message.error(error.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  // 取消任务
  const handleCancelTask = async (taskId) => {
    try {
      const response = await cancelTask(taskId);
      if (response.success) {
        message.success('任务已取消');
        loadTasks();
      }
    } catch (error) {
      message.error('取消失败');
    }
  };

  // 重试任务
  const handleRetryTask = async (taskId) => {
    try {
      const response = await retryTask(taskId);
      if (response.success) {
        message.success('任务已重新加入队列');
        loadTasks();
      }
    } catch (error) {
      message.error('重试失败');
    }
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName'
    },
    {
      title: '视频标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true
    },
    {
      title: '发布账号数',
      dataIndex: 'accountIds',
      key: 'accountCount',
      render: (ids) => {
        const arr = JSON.parse(ids || '[]');
        return arr.length;
      }
    },
    {
      title: '发布模式',
      dataIndex: 'publishMode',
      key: 'publishMode',
      render: (mode) => {
        const modeMap = {
          immediate: <Tag color="green">立即发布</Tag>,
          scheduled: <Tag color="blue"><ClockCircleOutlined /> 定时发布</Tag>,
          scheduled_audit: <Tag color="orange">定时待审核</Tag>
        };
        return modeMap[mode] || mode;
      }
    },
    {
      title: '计划时间',
      dataIndex: 'scheduledTime',
      key: 'scheduledTime',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          pending: { color: 'processing', text: '待发布' },
          processing: { color: 'processing', text: '发布中' },
          completed: { color: 'success', text: '已完成' },
          partial_success: { color: 'warning', text: '部分成功' },
          failed: { color: 'error', text: '失败' },
          cancelled: { color: 'default', text: '已取消' }
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '审核状态',
      dataIndex: 'auditStatus',
      key: 'auditStatus',
      render: (status) => {
        if (!status) return '-';
        const statusMap = {
          pending: <Tag color="warning">待审核</Tag>,
          approved: <Tag color="success"><CheckCircleOutlined /> 通过</Tag>,
          rejected: <Tag color="error"><CloseCircleOutlined /> 拒绝</Tag>
        };
        return statusMap[status] || status;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => {}}>查看</Button>
          {record.status === 'pending' && (
            <Button type="link" danger size="small" onClick={() => handleCancelTask(record.id)}>
              取消
            </Button>
          )}
          {record.status === 'failed' && (
            <Button type="link" size="small" onClick={() => handleRetryTask(record.id)}>
              重试
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title="批量发布管控系统"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            创建发布任务
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 创建任务弹窗 */}
      <Modal
        title="创建发布任务"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          onFinish={handleCreateTask}
          layout="vertical"
        >
          <Form.Item
            name="taskName"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="例如：美食视频批量发布" />
          </Form.Item>

          <Form.Item
            name="videoId"
            label="选择视频"
            rules={[{ required: true, message: '请选择视频' }]}
          >
            <Select placeholder="选择要发布的视频" showSearch>
              {videos.map(v => (
                <Option key={v.id} value={v.id}>{v.title || v.videoUrl}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="发布标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="视频标题" />
          </Form.Item>

          <Form.Item
            name="copywriting"
            label="发布文案"
          >
            <TextArea rows={4} placeholder="视频描述文案" />
          </Form.Item>

          <Form.Item
            name="accountIds"
            label="选择发布账号"
            rules={[{ required: true, message: '请选择账号' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择要发布的抖音账号"
              maxTagCount="responsive"
            >
              {accounts.map(a => (
                <Option key={a.id} value={a.id}>
                  {a.nickname} ({a.followerCount > 10000 ? `${(a.followerCount/10000).toFixed(1)}w` : a.followerCount}粉丝)
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item
            name="publishMode"
            label="发布模式"
            initialValue="immediate"
          >
            <Select>
              <Option value="immediate">立即发布</Option>
              <Option value="scheduled">定时发布</Option>
              <Option value="scheduled_audit">定时待审核</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.publishMode !== curr.publishMode}
          >
            {({ getFieldValue }) =>
              ['scheduled', 'scheduled_audit'].includes(getFieldValue('publishMode')) && (
                <Form.Item
                  name="scheduledTime"
                  label="计划发布时间"
                  rules={[{ required: true, message: '请选择时间' }]}
                >
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="publishInterval"
            label="发布间隔（秒）"
            initialValue={5}
          >
            <InputNumber min={1} max={3600} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              创建任务
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PublishManage;
